import { useState, useEffect, useRef } from "react";
import {
  getDashboardOverview,
  getCampaigns,
  getCampaignTrends,
  getTwilioMetrics,
} from "../api/DashboardApi";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  PieChart, Pie, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from "recharts";
import {
  Mail, MousePointer, FileWarning, Flag,
  Users, Target, TrendingUp, ArrowRight, BarChart3, RefreshCw,
  Calendar, MessageSquare, CheckCircle2, XCircle, Clock
} from "lucide-react";
import { toast } from "sonner";
import CulsightLoader from "../components/ui/CulsightLoader";

const COLORS = {
  purple: "#8c3add",
  purpleLight: "#ba85ed",
  teal: "#1fa7a1",
  tealLight: "#77c6c5",
  yellow: "#f59e0b",
  red: "#ef4444",
  gray: "#e5e7eb",
  skyBlue: "#3b82f6"
};

// Custom X-axis tick: campaign id se point banta hai, label me name dikhta hai
const TrendTick = ({ x, y, payload, nameMap }) => {
  const full = nameMap[payload.value] || "";
  const label = full.length > 12 ? full.slice(0, 11) + "…" : full;
  return (
    <g transform={`translate(${x},${y})`}>
      <title>{full}</title>
      <text
        x={0}
        y={0}
        dy={10}
        textAnchor="end"
        fill="#666"
        fontSize={11}
        transform="rotate(-20)"
      >
        {label}
      </text>
    </g>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [trends, setTrends] = useState([]);
  const [twilioMetrics, setTwilioMetrics] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const hasLoadedOnce = useRef(false);
  const requestIdRef = useRef(0);
  const [dateRange, setDateRange] = useState("7");
  const [chartType, setChartType] = useState("line");

  useEffect(() => {
    fetchData();
  }, [dateRange]);

  const fetchData = async () => {
    const myRequestId = ++requestIdRef.current;

    const isFirstLoad = !hasLoadedOnce.current;

    if (isFirstLoad) {
      setInitialLoading(true);
    } else {
      setRefreshing(true);
    }

    const startTime = Date.now();

    try {
      const [overviewRes, campaignsRes, trendsRes, twilioRes] =
        await Promise.all([
          getDashboardOverview(),
          getCampaigns(),
          getCampaignTrends(dateRange),
          getTwilioMetrics(),
        ]);

      // Ignore old API response
      if (myRequestId !== requestIdRef.current) return;

      setOverview(overviewRes || {});
      setCampaigns(
        Array.isArray(campaignsRes)
          ? campaignsRes.slice(0, 5)
          : []
      );
      setTrends(
        Array.isArray(trendsRes?.trends)
          ? trendsRes.trends
          : []
      );
      setTwilioMetrics(twilioRes || null);

    } catch (error) {
      if (myRequestId === requestIdRef.current) {
        console.error("Dashboard API Error:", error);
        toast.error("Failed to load dashboard data");
      }

    } finally {
      const elapsed = Date.now() - startTime;
      const minDelay = 300;

      if (elapsed < minDelay) {
        await new Promise((resolve) =>
          setTimeout(resolve, minDelay - elapsed)
        );
      }

      if (myRequestId === requestIdRef.current) {
        hasLoadedOnce.current = true;
        setInitialLoading(false);
        setRefreshing(false);
      }
    }
  };

  if (initialLoading) {
    return (
      <div data-testid="dashboard-loading">
        <CulsightLoader variant="page" />
      </div>
    );
  }

  const metrics = overview?.metrics || {};
  const rates = overview?.rates || {};

  // ---- Trends chart data (id based) ----
  const chartData = trends.map((t, i) => ({
    ...t,
    key: String(t.id ?? t.campaign_id ?? i), // unique id
  }));
  const nameMap = Object.fromEntries(chartData.map((t) => [t.key, t.name]));

  const commonXAxis = (
    <XAxis
      dataKey="key"
      interval={0}
      height={60}
      tickLine={false}
      padding={{ left: 40, right: 20 }}
      tick={<TrendTick nameMap={nameMap} />}
    />
  );
  const commonMargin = { top: 10, right: 20, left: 20, bottom: 10 };
  const tooltipLabel = (key) => nameMap[key] || key;

  const donutData = [
    { name: "Email Sent", value: metrics.emails_sent || 0, rate: rates.sent_rate || 0, color: COLORS.skyBlue },
    { name: "Email Opened", value: metrics.emails_opened || 0, rate: rates.open_rate || 0, color: COLORS.teal },
    { name: "Clicked Link", value: metrics.links_clicked || 0, rate: rates.click_rate || 0, color: COLORS.yellow },
    { name: "Submitted Data", value: metrics.data_submitted || 0, rate: rates.submit_rate || 0, color: COLORS.red },
    { name: "Reported", value: metrics.reported_spam || 0, rate: rates.report_rate || 0, color: COLORS.purple }
  ];

  const DonutChart = ({ data, size = 120 }) => {
    const total = data.value;
    const percentage = data.rate;

    return (
      <div className="flex flex-col items-center">
        <div className="relative" style={{ width: size, height: size }}>
          <PieChart width={size} height={size}>
            <Pie
              data={[
                { value: percentage, fill: data.color },
                { value: Math.max(0, 100 - percentage), fill: COLORS.gray }
              ]}
              cx="50%"
              cy="50%"
              innerRadius={size * 0.35}
              outerRadius={size * 0.45}
              startAngle={90}
              endAngle={-270}
              dataKey="value"
            />
          </PieChart>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-lg font-bold text-[#1f1f23]">{percentage}%</span>
          </div>
        </div>
        <p className="mt-2 text-xs font-medium text-center" style={{ color: data.color }}>
          {data.name} ({total})
        </p>
      </div>
    );
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active": return "bg-[#1fa7a1] text-white";
      case "scheduled": return "bg-[#8c3add] text-white";
      case "paused": return "bg-amber-500 text-white";
      case "completed": return "bg-gray-400 text-white";
      default: return "bg-gray-100 text-gray-600";
    }
  };

  return (
    <div
      className="space-y-6 animate-fadeIn relative min-h-screen"
      data-testid="dashboard-page"
    >
      {refreshing && (
        <CulsightLoader
          variant="overlay"
          label="Updating dashboard..."
        />
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1f1f23] tracking-tight">Overview</h1>
          <p className="text-gray-500 text-sm mt-1">Monitor your phishing simulation performance</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-600">
            Total Users: <strong className="text-[#1f1f23]">{overview?.total_users || 0}</strong>
          </span>
          <span className="text-sm text-gray-600">
            Active Campaigns: <strong className="text-[#1fa7a1]">{overview?.active_campaigns || 0}</strong>
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchData}
            className="gap-2"
            data-testid="refresh-button"
          >
            <RefreshCw size={16} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Donut Charts Row */}
      <Card className="border border-gray-100 shadow-sm" data-testid="metrics-card">
        <CardContent className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {donutData.map((item, index) => (
              <DonutChart key={index} data={item} />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { icon: Mail, label: "Open Rate", value: `${rates.open_rate || 0}%`, color: COLORS.teal, bgColor: "bg-teal-50" },
          { icon: MousePointer, label: "Click Rate", value: `${rates.click_rate || 0}%`, color: COLORS.yellow, bgColor: "bg-amber-50" },
          { icon: FileWarning, label: "Submit Rate", value: `${rates.submit_rate || 0}%`, color: COLORS.red, bgColor: "bg-red-50" },
          { icon: Flag, label: "Report Rate", value: `${rates.report_rate || 0}%`, color: COLORS.purple, bgColor: "bg-purple-50" }
        ].map((stat, index) => (
          <Card
            key={index}
            className="border border-gray-100 shadow-sm card-hover cursor-pointer"
            data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}
          >
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 font-medium">{stat.label}</p>
                  <p className="text-2xl font-bold mt-1" style={{ color: stat.color }}>
                    {stat.value}
                  </p>
                </div>
                <div className={`p-3 rounded-xl ${stat.bgColor}`}>
                  <stat.icon size={24} style={{ color: stat.color }} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Campaign Trends Chart */}
      <Card className="border border-gray-100 shadow-sm" data-testid="trends-card">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-lg font-semibold text-[#1f1f23]">Campaign Performance Trends</CardTitle>
          <div className="flex items-center gap-2">
            <Select value={dateRange} onValueChange={setDateRange}>
              <SelectTrigger className="w-[140px] h-9">
                <Calendar size={14} className="mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 3 months</SelectItem>
              </SelectContent>
            </Select>
            <Select value={chartType} onValueChange={setChartType}>
              <SelectTrigger className="w-[100px] h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="line">Line</SelectItem>
                <SelectItem value="bar">Bar</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          {trends.length === 0 ? (
            <div className="h-[300px] flex items-center justify-center text-gray-500">
              No trend data available
            </div>
          ) : (
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                {chartType === "line" ? (
                  <LineChart data={chartData} margin={commonMargin}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    {commonXAxis}
                    <YAxis tick={{ fontSize: 11 }} domain={[0, 100]} />
                    <Tooltip
                      labelFormatter={tooltipLabel}
                      contentStyle={{
                        backgroundColor: "white",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      }}
                    />
                    <Legend />
                    <Line type="monotone" dataKey="open_rate" name="Open %" stroke={COLORS.teal} strokeWidth={2} dot={{ r: 4 }} />
                    <Line type="monotone" dataKey="click_rate" name="Click %" stroke={COLORS.yellow} strokeWidth={2} dot={{ r: 4 }} />
                    <Line type="monotone" dataKey="submit_rate" name="Submit %" stroke={COLORS.red} strokeWidth={2} dot={{ r: 4 }} />
                    <Line type="monotone" dataKey="report_rate" name="Report %" stroke={COLORS.purple} strokeWidth={2} dot={{ r: 4 }} />
                  </LineChart>
                ) : (
                  <BarChart data={chartData} margin={commonMargin}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    {commonXAxis}
                    <YAxis tick={{ fontSize: 11 }} domain={[0, 100]} />
                    <Tooltip
                      labelFormatter={tooltipLabel}
                      contentStyle={{
                        backgroundColor: "white",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                      }}
                    />
                    <Legend />
                    <Bar dataKey="open_rate" name="Open %" fill={COLORS.teal} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="click_rate" name="Click %" fill={COLORS.yellow} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="submit_rate" name="Submit %" fill={COLORS.red} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="report_rate" name="Report %" fill={COLORS.purple} radius={[4, 4, 0, 0]} />
                  </BarChart>
                )}
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Access Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { title: "Department Heatmap", desc: "View risk by department", icon: Target, path: "/department-heatmap", color: COLORS.purple },
          { title: "Human Risk Scorecard", desc: "Individual risk scores", icon: Users, path: "/human-risk-scorecard", color: COLORS.teal },
          { title: "Insider Risk", desc: "Behavioral indicators", icon: TrendingUp, path: "/insider-risk", color: COLORS.red }
        ].map((card, index) => (
          <Card
            key={index}
            className="border border-gray-100 shadow-sm card-hover cursor-pointer group"
            onClick={() => navigate(card.path)}
            data-testid={`quick-access-${card.title.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <CardContent className="p-5 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl" style={{ backgroundColor: `${card.color}15` }}>
                  <card.icon size={22} style={{ color: card.color }} />
                </div>
                <div>
                  <p className="font-semibold text-[#1f1f23]">{card.title}</p>
                  <p className="text-sm text-gray-500">{card.desc}</p>
                </div>
              </div>
              <ArrowRight size={20} className="text-gray-400 group-hover:text-[#8c3add] group-hover:translate-x-1 transition-all" />
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Latest Campaign Table */}
      <Card className="border border-gray-100 shadow-sm" data-testid="campaigns-table-card">
        <CardHeader className="flex flex-row items-center justify-between pb-4">
          <CardTitle className="text-lg font-semibold text-[#1f1f23]">Latest Campaigns</CardTitle>
          <Button
            className="bg-[#8c3add] hover:bg-[#702eb0] text-white"
            onClick={() => navigate("/campaigns")}
            data-testid="view-all-campaigns"
          >
            All Campaigns
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="text-left py-3 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="text-left py-3 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">User Group</th>
                  <th className="text-left py-3 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Sent</th>
                  <th className="text-left py-3 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Opened</th>
                  <th className="text-left py-3 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Clicked</th>
                  <th className="text-left py-3 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Reported</th>
                  <th className="text-left py-3 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="text-left py-3 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="py-12 text-center text-gray-500">
                      No campaigns found
                    </td>
                  </tr>
                ) : (
                  campaigns.map((campaign, index) => (
                    <tr
                      key={campaign.id}
                      className="border-b border-gray-50 hover:bg-[#8c3add]/[0.02] transition-colors"
                      data-testid={`campaign-row-${index}`}
                    >
                      <td className="py-4 px-6 font-medium text-[#1f1f23]">{campaign.name}</td>
                      <td className="py-4 px-6 text-gray-600">{campaign.user_group}</td>
                      <td className="py-4 px-6 text-gray-600">{campaign.emails_sent}</td>
                      <td className="py-4 px-6 text-[#1fa7a1] font-medium">{campaign.emails_opened}</td>
                      <td className="py-4 px-6 text-[#f59e0b] font-medium">{campaign.links_clicked}</td>
                      <td className="py-4 px-6 text-[#8c3add] font-medium">{campaign.reported_spam}</td>
                      <td className="py-4 px-6">
                        <Badge className={getStatusColor(campaign.status)}>
                          {campaign.status?.charAt(0).toUpperCase() + campaign.status?.slice(1)}
                        </Badge>
                      </td>
                      <td className="py-4 px-6">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="p-2 hover:bg-[#8c3add]/10"
                          onClick={() => navigate(`/campaigns/${campaign.id}`)}
                          data-testid={`campaign-action-${index}`}
                        >
                          <BarChart3 size={18} className="text-[#8c3add]" />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Twilio Delivery Metrics (SMS + WhatsApp) */}
      {twilioMetrics && twilioMetrics.total_events > 0 && (
        <Card className="border border-gray-100 shadow-sm" data-testid="twilio-metrics-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <MessageSquare size={20} className="text-[#1fa7a1]" />
                SMS / WhatsApp Delivery
              </CardTitle>
              <Badge variant="outline">{twilioMetrics.total_events} total events</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Top KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div className="p-3 rounded-xl bg-green-50 flex items-center gap-3" data-testid="kpi-delivered">
                <div className="p-2 rounded-lg bg-green-100"><CheckCircle2 size={18} className="text-green-700" /></div>
                <div>
                  <p className="text-xl font-bold text-green-800">{twilioMetrics.delivered}</p>
                  <p className="text-xs text-green-700/80">Delivered</p>
                </div>
              </div>
              <div className="p-3 rounded-xl bg-red-50 flex items-center gap-3" data-testid="kpi-failed">
                <div className="p-2 rounded-lg bg-red-100"><XCircle size={18} className="text-red-700" /></div>
                <div>
                  <p className="text-xl font-bold text-red-800">{twilioMetrics.failed}</p>
                  <p className="text-xs text-red-700/80">Failed / Undelivered</p>
                </div>
              </div>
              <div className="p-3 rounded-xl bg-amber-50 flex items-center gap-3" data-testid="kpi-inflight">
                <div className="p-2 rounded-lg bg-amber-100"><Clock size={18} className="text-amber-700" /></div>
                <div>
                  <p className="text-xl font-bold text-amber-800">{twilioMetrics.in_flight}</p>
                  <p className="text-xs text-amber-700/80">In flight</p>
                </div>
              </div>
              <div className="p-3 rounded-xl bg-[#1fa7a1]/10 flex items-center gap-3" data-testid="kpi-delivery-rate">
                <div className="p-2 rounded-lg bg-[#1fa7a1]/20"><TrendingUp size={18} className="text-[#1fa7a1]" /></div>
                <div>
                  <p className="text-xl font-bold text-[#1fa7a1]">{twilioMetrics.delivery_rate}%</p>
                  <p className="text-xs text-[#1fa7a1]/80">Delivery rate</p>
                </div>
              </div>
              <div className="p-3 rounded-xl bg-gray-50 flex items-center gap-3" data-testid="kpi-failure-rate">
                <div className="p-2 rounded-lg bg-gray-100"><FileWarning size={18} className="text-gray-700" /></div>
                <div>
                  <p className="text-xl font-bold text-gray-800">{twilioMetrics.failure_rate}%</p>
                  <p className="text-xs text-gray-600">Failure rate</p>
                </div>
              </div>
            </div>

            {/* Per-campaign breakdown + Top errors */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Per-campaign breakdown</h4>
                {twilioMetrics.by_campaign.length === 0 ? (
                  <p className="text-xs text-gray-500 p-3 border rounded">No campaign-tagged events yet.</p>
                ) : (
                  <div className="space-y-2">
                    {twilioMetrics.by_campaign.map((c) => (
                      <div
                        key={c.campaign_id}
                        className="p-3 border border-gray-100 rounded-lg cursor-pointer hover:bg-gray-50"
                        onClick={() => navigate(`/campaigns/${c.campaign_id}`)}
                        data-testid={`twilio-campaign-${c.campaign_id}`}
                      >
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <p className="text-sm font-medium text-[#1f1f23] truncate">{c.name}</p>
                          <Badge variant="outline" className="text-xs capitalize">{c.simulation_type}</Badge>
                        </div>
                        <div className="flex items-center gap-3 text-xs">
                          <span className="text-green-700">✓ {c.delivered}</span>
                          <span className="text-red-700">✗ {c.failed}</span>
                          <span className="text-gray-500">Total {c.total}</span>
                          <span className="ml-auto font-semibold text-[#1fa7a1]">{c.delivery_rate}% delivered</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-1.5 mt-2">
                          <div className="h-1.5 rounded-full bg-[#1fa7a1]" style={{ width: `${c.delivery_rate}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Top Twilio error codes</h4>
                {twilioMetrics.top_errors.length === 0 ? (
                  <p className="text-xs text-gray-500 p-3 border rounded">No errors recorded</p>
                ) : (
                  <div className="space-y-2">
                    {twilioMetrics.top_errors.map((e) => (
                      <div key={e.code} className="flex items-center justify-between p-2 border rounded text-xs">
                        <code className="text-red-700 font-mono">code {e.code}</code>
                        <Badge className="bg-red-100 text-red-700">{e.count}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Dashboard;